import { useRef, useEffect, useState } from 'react';
import { CalendarPreviewProps } from '../../types';

/**
 * Mirrors legacy print-on-demand CSS overlays exactly.
 *
 * Supported styles and their reference container widths:
 *   calendar-style-1   600px  top:475px left:50%          Bentonsans  black   TX phone format
 *   calendar-style-2   600px  top:475px left:33% w:65%    Helvetica   #87212e GA phone format
 *   5333-stickup-tx    700px  top:9%    left:260px         Bentonsans  #3e3e3e TX
 *   5333-stickup-ga    700px  top:9%    left:0 (name abs)  Helvetica   #813037 GA
 *   6552-classic-tx    880px  top:50px  left:46%           Bentonsans  #9e8a57 TX
 *   6552-classic-ga    880px  top:28px  left:15%           Helvetica   #d3c666 GA
 *   7726-mini-tx       880px  top:87%   left:57%           Bentonsans  #3e3e3e TX
 *   7726-mini-ga       880px  bot:80px  left:18%           Helvetica   #7f2b45 GA
 *   8203-monthly-tx    500px  top:155 left:194px           Bentonsans  #968a4d TX
 *   8203-monthly-ga    500px  top:175px left:10px          Helvetica   #968a4d GA
 *   8051-weekly-tx     400px  top:510px left:57px          Bentonsans  #968a4d TX
 *   8051-weekly-ga     400px  top:460px left:0             Helvetica   #968a4d GA
 *
 * Scaling: all pixel values scale linearly with containerWidth / config.containerWidth.
 * Percentage top/bottom values are left as-is (already responsive).
 */

interface StyleConfig {
  containerWidth: number;
  topPx?: number;
  topPct?: string;
  bottomPx?: number;
  leftPx?: number;
  leftPct?: string;
  overlayWidth?: string;
  textAlign: 'left' | 'center' | 'right';
  color: string;
  baseFontSize: number;
  baseLineHeight: number;
  nameFontSize: number;
  nameMarginTop?: number;
  nameMarginBottom?: number;
  nameLineHeight?: number;
  nameAbsoluteLeft?: string;
  nameMaxWidth?: string;
  addressMarginTop?: number;
  phoneMarginTop?: number;
  phonePrefix: 'space' | 'colon';
  phonePadRight?: number;
  agencyLineHeight?: number;
}

const STYLE_CONFIGS: Record<string, StyleConfig> = {
  'calendar-style-1': {
    containerWidth: 600,
    topPx: 475,
    leftPct: '50%',
    textAlign: 'left',
    color: '#000000',
    baseFontSize: 9,
    baseLineHeight: 12,
    nameFontSize: 14,
    phoneMarginTop: 2,
    phonePrefix: 'space',
  },
  'calendar-style-2': {
    containerWidth: 600,
    topPx: 475,
    leftPct: '33%',
    overlayWidth: '65%',
    textAlign: 'center',
    color: '#87212e',
    baseFontSize: 10,
    baseLineHeight: 10,
    nameFontSize: 14,
    nameMarginBottom: 5,
    agencyLineHeight: 13,
    phoneMarginTop: 0,
    phonePrefix: 'colon',
  },
  '5333-stickup-tx': {
    containerWidth: 700,
    topPct: '9%',
    leftPx: 260,
    overlayWidth: 'calc(100% - 270px)',
    textAlign: 'left',
    color: '#3e3e3e',
    baseFontSize: 20,
    baseLineHeight: 0,
    nameFontSize: 26,
    nameMarginTop: 160,
    phoneMarginTop: 0,
    phonePrefix: 'space',
    phonePadRight: 8,
  },
  '5333-stickup-ga': {
    containerWidth: 700,
    topPct: '9%',
    leftPx: 0,
    overlayWidth: '100%',
    textAlign: 'center',
    color: '#813037',
    baseFontSize: 18,
    baseLineHeight: 0,
    nameFontSize: 26,
    nameAbsoluteLeft: '50%',
    nameMaxWidth: '44%',
    addressMarginTop: 120,
    phoneMarginTop: 0,
    phonePrefix: 'colon',
    phonePadRight: 8,
  },
  '6552-classic-tx': {
    containerWidth: 880,
    topPx: 50,
    leftPct: '46%',
    overlayWidth: '100%',
    textAlign: 'left',
    color: '#9e8a57',
    baseFontSize: 10,
    baseLineHeight: 12,
    nameFontSize: 16,
    phoneMarginTop: 0,
    phonePrefix: 'space',
    phonePadRight: 5,
  },
  '6552-classic-ga': {
    containerWidth: 880,
    topPx: 28,
    leftPct: '15%',
    overlayWidth: '100%',
    textAlign: 'center',
    color: '#d3c666',
    baseFontSize: 10,
    baseLineHeight: 12,
    nameFontSize: 20,
    phoneMarginTop: 0,
    phonePrefix: 'colon',
    phonePadRight: 5,
  },
  '7726-mini-tx': {
    containerWidth: 880,
    topPct: '87%',
    leftPct: '57%',
    overlayWidth: '100%',
    textAlign: 'left',
    color: '#3e3e3e',
    baseFontSize: 13,
    baseLineHeight: 16,
    nameFontSize: 24,
    phoneMarginTop: 0,
    phonePrefix: 'space',
    phonePadRight: 8,
  },
  '7726-mini-ga': {
    containerWidth: 880,
    bottomPx: 80,
    leftPct: '18%',
    overlayWidth: '100%',
    textAlign: 'center',
    color: '#7f2b45',
    baseFontSize: 13,
    baseLineHeight: 16,
    nameFontSize: 24,
    nameMarginBottom: 5,
    phoneMarginTop: 0,
    phonePrefix: 'colon',
    phonePadRight: 8,
  },
  '8203-monthly-tx': {
    containerWidth: 500,
    topPx: 155,
    leftPx: 194,
    overlayWidth: '100%',
    textAlign: 'left',
    color: '#968a4d',
    baseFontSize: 12,
    baseLineHeight: 15,
    nameFontSize: 16,
    phoneMarginTop: 0,
    phonePrefix: 'space',
    phonePadRight: 8,
  },
  '8203-monthly-ga': {
    containerWidth: 500,
    topPx: 175,
    leftPx: 10,
    overlayWidth: '100%',
    textAlign: 'center',
    color: '#968a4d',
    baseFontSize: 10,
    baseLineHeight: 14,
    nameFontSize: 20,
    nameMarginBottom: 5,
    addressMarginTop: 15,
    phoneMarginTop: 0,
    phonePrefix: 'colon',
    phonePadRight: 8,
  },
  '8051-weekly-tx': {
    containerWidth: 400,
    topPx: 510,
    leftPx: 57,
    overlayWidth: 'calc(100% - 60px)',
    textAlign: 'left',
    color: '#968a4d',
    baseFontSize: 14,
    baseLineHeight: 20,
    nameFontSize: 24,
    nameMarginBottom: 10,
    nameLineHeight: 22,
    phoneMarginTop: 0,
    phonePrefix: 'space',
    phonePadRight: 8,
  },
  '8051-weekly-ga': {
    containerWidth: 400,
    topPx: 460,
    leftPx: 0,
    overlayWidth: '100%',
    textAlign: 'center',
    color: '#968a4d',
    baseFontSize: 14,
    baseLineHeight: 16,
    nameFontSize: 24,
    phoneMarginTop: 0,
    phonePrefix: 'colon',
    phonePadRight: 8,
  },
};

const DEFAULT_CONFIG: StyleConfig = {
  containerWidth: 600,
  topPx: 475,
  leftPct: '50%',
  textAlign: 'left',
  color: '#000000',
  baseFontSize: 9,
  baseLineHeight: 12,
  nameFontSize: 14,
  phoneMarginTop: 2,
  phonePrefix: 'space',
};

const NAME_FIELDS = new Set([
  'agency_combine', 'agency_name', 'property', 'individual_name', 'company_name',
]);
const CITY_GROUP  = new Set(['city', 'state', 'zip_code']);
const PHONE_GROUP = new Set(['phone', 'cell_phone', 'fax']);
const PHONE_LABEL: Record<string, string> = { phone: 'O', cell_phone: 'M', fax: 'F' };
const PHONE_ORDER = ['phone', 'cell_phone', 'fax'];

const DEFAULT_FIELD_KEYS = [
  'agency_combine', 'additional_agents', 'address',
  'city', 'state', 'zip_code',
  'phone', 'cell_phone', 'fax',
  'other_info',
];

export function CalendarPreview({ imageUrl, location, altText, style, fields }: CalendarPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(600);

  useEffect(() => {
    if (!containerRef.current) return;
    const update = () => setContainerWidth(containerRef.current?.offsetWidth || 600);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  if (!imageUrl) return null;

  const config = (style && STYLE_CONFIGS[style]) || DEFAULT_CONFIG;
  const scale = containerWidth / config.containerWidth;
  const fontSize = config.baseFontSize * scale;
  const lineHeight = config.baseLineHeight > 0 ? config.baseLineHeight * scale : undefined;

  const locValues = location as unknown as Record<string, string>;
  const orderedKeys = fields ? fields.map(f => f.key) : DEFAULT_FIELD_KEYS;

  const cityVal  = locValues.city     || '';
  const stateVal = locValues.state    || '';
  const zipVal   = locValues.zip_code || '';
  const hasCityRow    = !!(cityVal || stateVal || zipVal);
  const showCityComma = !!(stateVal || zipVal);

  const filledPhones = PHONE_ORDER.filter(k => locValues[k]);
  const isMultiPhone = filledPhones.length > 1;
  const phonePadRight = config.phonePadRight ?? 5;

  type RenderItem =
    | { type: 'name';      key: string; value: string }
    | { type: 'address';   value: string }
    | { type: 'address2';  value: string }
    | { type: 'title';     value: string }
    | { type: 'slight_bar' }
    | { type: 'city_row' }
    | { type: 'phone_row' }
    | { type: 'plain';     key: string; value: string };

  const renderItems: RenderItem[] = [];
  const firedGroups = new Set<string>();

  for (const key of orderedKeys) {
    if (CITY_GROUP.has(key)) {
      if (!firedGroups.has('city') && hasCityRow) {
        firedGroups.add('city');
        renderItems.push({ type: 'city_row' });
      }
      continue;
    }

    if (PHONE_GROUP.has(key)) {
      if (!firedGroups.has('phone') && filledPhones.length > 0) {
        firedGroups.add('phone');
        renderItems.push({ type: 'phone_row' });
      }
      continue;
    }

    if (key === 'slight_bar') {
      renderItems.push({ type: 'slight_bar' });
      continue;
    }

    const value = locValues[key] || '';

    if (NAME_FIELDS.has(key)) {
      if (value) renderItems.push({ type: 'name', key, value });
      continue;
    }

    if (key === 'address') {
      if (value) renderItems.push({ type: 'address', value });
      continue;
    }

    if (key === 'address2') {
      if (value) renderItems.push({ type: 'address2', value });
      continue;
    }

    if (key === 'title') {
      if (value) renderItems.push({ type: 'title', value });
      continue;
    }

    if (value) renderItems.push({ type: 'plain', key, value });
  }

  // --- overlay position ---
  const overlayPosition: React.CSSProperties = { position: 'absolute' };

  if (config.topPct !== undefined) {
    overlayPosition.top = config.topPct;
  } else if (config.topPx !== undefined) {
    overlayPosition.top = `${config.topPx * scale}px`;
  }

  if (config.bottomPx !== undefined) {
    overlayPosition.bottom = `${config.bottomPx * scale}px`;
  }

  if (config.leftPct !== undefined) {
    overlayPosition.left = config.leftPct;
  } else if (config.leftPx !== undefined) {
    overlayPosition.left = `${config.leftPx * scale}px`;
  }

  if (config.overlayWidth) overlayPosition.width = config.overlayWidth;

  const overlayStyle: React.CSSProperties = {
    ...overlayPosition,
    textAlign: config.textAlign,
    color: config.color,
    fontSize: `${fontSize}px`,
    ...(lineHeight !== undefined && { lineHeight: `${lineHeight}px` }),
  };

  // Whether name fields use absolute positioning (5333-stickup-ga)
  const nameIsAbsolute = !!config.nameAbsoluteLeft;

  return (
    <div ref={containerRef} className="relative rounded-xl overflow-hidden border border-gray-200 shadow-sm">
      <img src={imageUrl} alt={altText} className="w-full h-auto block" />
      <div style={overlayStyle}>
        {renderItems.map((item, idx) => {
          if (item.type === 'name') {
            const nameStyle: React.CSSProperties = {
              fontSize: `${config.nameFontSize * scale}px`,
              fontWeight: 'bold',
              textTransform: 'uppercase',
              ...(config.nameLineHeight !== undefined && { lineHeight: `${config.nameLineHeight * scale}px` }),
              ...(config.nameMarginBottom !== undefined && { marginBottom: `${config.nameMarginBottom * scale}px` }),
              ...(config.nameMarginTop !== undefined && { marginTop: `${config.nameMarginTop * scale}px` }),
              ...(item.key === 'agency_combine' && config.agencyLineHeight
                ? { lineHeight: `${config.agencyLineHeight * scale}px` }
                : {}),
              ...(nameIsAbsolute && {
                position: 'absolute',
                left: config.nameAbsoluteLeft,
                maxWidth: config.nameMaxWidth,
                textAlign: 'center',
              }),
            };
            return <div key={item.key} style={nameStyle}>{item.value}</div>;
          }

          if (item.type === 'slight_bar') {
            return (
              <div
                key={`slight_bar_${idx}`}
                style={{
                  width: `${50 * scale}px`,
                  margin: `${10 * scale}px 0`,
                  borderTop: `1px solid ${config.color}`,
                }}
              />
            );
          }

          if (item.type === 'title') {
            return (
              <div key="title" style={{ fontWeight: 'bold', textTransform: 'uppercase', marginTop: `${6 * scale}px` }}>
                {item.value}
              </div>
            );
          }

          if (item.type === 'address') {
            const addrStyle: React.CSSProperties = {
              textTransform: 'capitalize',
              ...(config.addressMarginTop !== undefined
                ? { marginTop: `${config.addressMarginTop * scale}px` }
                : { marginTop: `${2 * scale}px` }),
            };
            return <div key="address" style={addrStyle}>{item.value}</div>;
          }

          if (item.type === 'address2') {
            return <div key="address2">{item.value}</div>;
          }

          if (item.type === 'city_row') {
            return (
              <div key="city_row">
                {cityVal && (
                  <span style={{ display: 'inline-block', textTransform: 'capitalize' }}>
                    {cityVal}{showCityComma ? ',' : ''}
                  </span>
                )}
                {stateVal && (
                  <span style={{ display: 'inline-block', paddingLeft: `${2 * scale}px` }}>
                    {stateVal}
                  </span>
                )}
                {zipVal && (
                  <span style={{ display: 'inline-block', paddingLeft: `${2 * scale}px` }}>
                    {zipVal}
                  </span>
                )}
              </div>
            );
          }

          if (item.type === 'phone_row') {
            return (
              <div
                key="phone_row"
                style={config.phoneMarginTop
                  ? { marginTop: `${config.phoneMarginTop * scale}px` }
                  : undefined}
              >
                {PHONE_ORDER.map(key => {
                  const val = locValues[key];
                  if (!val) return null;
                  const label = PHONE_LABEL[key];

                  if (!isMultiPhone) {
                    return <div key={key}>{val}</div>;
                  }

                  if (config.phonePrefix === 'colon') {
                    return (
                      <span key={key} style={{ display: 'inline-block', paddingRight: `${phonePadRight * scale}px` }}>
                        <span style={{ fontWeight: 'bold' }}>{label}</span>
                        <span>: {val}</span>
                      </span>
                    );
                  }

                  return (
                    <span key={key} style={{ display: 'inline-block', paddingRight: `${phonePadRight * scale}px` }}>
                      <span style={{ fontWeight: 'bold' }}>{label} </span>
                      {val}
                    </span>
                  );
                })}
              </div>
            );
          }

          // plain
          return <div key={`${item.key}_${idx}`}>{item.value}</div>;
        })}
      </div>
    </div>
  );
}
